import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import { useButton, useDispatch, useI18n, useLogger, usePrinterApi } from '~Hooks';
import { BaseInputStyling, BlueButton, Box, GreenButton, RedButton } from '~Styles';
import { GetPrinters_venue_printers } from '../__queries__';
import Modal from '~ComponentsImproved/Modal';
import Heading from '~ComponentsImproved/Typography/Heading';
import { ModalContent } from '~Styles/modal';
import * as actions from '~actions';
import { handyBreakpoint } from '~Styles/breakpoints';
import { HeadingWrapper } from './AddedPrinter';

const Wrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  gap: 4px;
  padding: 10px 0;
  background-color: var(--colorBlueGray1100);
  color: var(--colorBlueGray100);
  align-items: center;
  padding: 8px 16px;
  margin-bottom: 16px;
  border-radius: 4px;

  label {
    display: block;
    font-weight: 600;
    color: var(--colorWhite);

    ${handyBreakpoint} {
      display: none;
    }
  }
`;

const Columns = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
  justify-content: space-between;
  padding-right: 10vw;

  ${handyBreakpoint} {
    padding-right: 8px;
    flex-direction: column;
    gap: 4px;
    justify-content: flex-start;
  }
`;

const Column = styled.div<{ width: number }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: ${({ width }) => width}px;

  ${handyBreakpoint} {
    &:first-child {
      font-weight: 600;
      width: 100%;
    }

    width: auto;
  }
`;

const NameInput = styled.input`
  ${BaseInputStyling}
  padding: 8px 16px;
`;

interface Props {
  id: string;
  name?: string;
  model: string;
  type: string;
  venuePrinter?: GetPrinters_venue_printers;
  updateVenuePrinter: (printerId: string, name: string, stations: string[], type: string) => void;
}

const PrinterToAdd: FC<Props> = ({ id, name, model, type, venuePrinter, updateVenuePrinter }) => {
  const logger = useLogger('settings-v2');
  const dispatch = useDispatch();
  const { i18n } = useI18n();
  const [showPrinterNameInput, setShowPrinterNameInput] = useState(false);
  const [newName, setNewName] = useState(name ?? '');

  function addPrinter(printerName?: string) {
    const printer = {
      id,
      name: printerName || id,
      model,
      type,
    };
    logger.info('Updating Settings (add printer)', { fields: { printer } });
    window.postMessageBridge({
      addPrinter: printer,
    });

    setShowPrinterNameInput(false);

    dispatch(actions.addToast(i18n('Settings.Printers.Toast.Added'), true));

    // Scroll to top
    const settingsWrapper = document.getElementById('settings-scroll-wrapper');
    if (settingsWrapper) {
      settingsWrapper.scrollTop = 0;
    }

    updateVenuePrinter(id, printerName ?? id, venuePrinter?.stations ?? [], type);
  }

  const onAddClick = () => {
    if (name && name !== id) {
      addPrinter(name);
      return;
    }

    setShowPrinterNameInput(true);
  };

  const addButton = useButton({
    onClick: onAddClick,
  });

  return (
    <>
      <Wrapper>
        <Columns>
          <Column width={200}>
            <label>{i18n('Settings.Printers.Address')}:</label>
            {id}
          </Column>
          {name && (
            <Column width={200}>
              <label>{i18n('Settings.Printers.Name')}:</label>
              {name}
            </Column>
          )}
          <Column width={120}>
            <label>{i18n('Settings.Printers.Model')}:</label>
            {model}
          </Column>
        </Columns>
        <Box display="flex" gap="small">
          <GreenButton {...addButton}>{i18n('Settings.AddPrinter')}</GreenButton>
        </Box>
      </Wrapper>

      {/* Printer name modal */}
      <Modal
        isShowing={showPrinterNameInput}
        hide={() => setShowPrinterNameInput(false)}
        position="flex-start">
        <ModalContent>
          <HeadingWrapper>
            <Heading level={2} color="#fff" bottom={16}>
              {i18n('Settings.Rename.Title')}
            </Heading>
          </HeadingWrapper>
          <Box marginBottom="medium">
            <NameInput
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
            />
          </Box>
          <BlueButton
            onClick={() => (newName ? addPrinter(newName) : undefined)}
            disabled={!newName}>
            {i18n('Settings.Rename.SubmitAndAdd')}
          </BlueButton>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PrinterToAdd;
